import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isNil } from 'lodash-es';

import { Order } from '../../objects/order';
import { Destination } from '../../objects/destination';
import { DateOfStay } from '../../objects/date-of-stay';
import { Product } from '../../objects/product';

@Injectable({
  providedIn: 'root'
})
export class RoutingActionService {

  private router: Router = inject(Router);

  navigateToProducts(payload: {
    order: Order;
    destination: Destination;
    dateOfStay: DateOfStay | null;
  }) {
    const {
      order,
      destination,
      dateOfStay,
    } = payload;

    const url = !isNil(dateOfStay) && dateOfStay.isStartAndEndSet() ?
      `widget/products/destination/${
        destination.slug }/order/${
        order.token }?start=${
        dateOfStay.start!.toISOString() }&end=${
        dateOfStay.end!.toISOString() }` :
      `widget/products/destination/${
        destination.slug }/order/${
        order.token }`;

    this.router.navigateByUrl(url);
  }

  navigateToRentalProductDatePickerPage(payload: {
    order: Order;
    product: Product;
    destination: Destination;
    dateOfStay: DateOfStay | null;
  }) {
    const {
      order,
      product,
      destination,
      dateOfStay,
    } = payload;

    const destinationSlug = destination.slug || destination.id;
    const productSlug = product.slug || product.id;
    
    const url = !isNil(dateOfStay) &&  dateOfStay.isStartAndEndSet() ?
      `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }/calendar?start=${
        dateOfStay.start!.toISOString() }&end=${
        dateOfStay.end!.toISOString() }`:
      `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }/calendar`;

    this.router.navigateByUrl(url);
  }

  navigateToProductAvailabilities(payload: {
    order: Order;
    product: Product;
    destination: Destination;
    dateOfStay: DateOfStay;
  }) {
    const {
      order,
      product,
      destination,
      dateOfStay,
    } = payload;

    const destinationSlug = destination.slug || destination.id;
    const productSlug = product.slug || product.id;
    
    if (!dateOfStay.isStartAndEndSet()) {
      return;
    }
    const url = `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }/availabilities?start=${
        dateOfStay.start!.toISOString() }&end=${
        dateOfStay.end!.toISOString() }`;

    this.router.navigateByUrl(url);
  }

  navigateToProductDetails(payload: {
    order: Order;
    product: Product;
    destination: Destination;
    dateOfStay: DateOfStay | null;
  }) {
    const {
      order,
      destination,
      product,
      dateOfStay,
    } = payload;
    const destinationSlug = destination.slug || destination.id;
    const productSlug = product.slug || product.id;

    const url = !isNil(dateOfStay) &&  dateOfStay.isStartAndEndSet() ?
      `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }?start=${
        dateOfStay.start!.toISOString() }&end=${
        dateOfStay.end!.toISOString() }`:
      `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }`;
    
    this.router.navigateByUrl(url);
  }


  navigateToRentalProductLayoutLayoutPage(payload: {
    order: Order;
    product: Product;
    destination: Destination;
    dateOfStay: DateOfStay;
    quantity: number;
  }) {
    const {
      order,
      product,
      destination,
      dateOfStay,
      quantity,
    } = payload;

    const destinationSlug = destination.slug || destination.id;
    const productSlug = product.slug || product.id;
    
    const url = `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }/layout?start=${
        dateOfStay.start!.toISOString() }&end=${
        dateOfStay.end!.toISOString() }&quantity=${
        quantity}`;

    this.router.navigateByUrl(url);
  }


  navigateToProductConditions(payload: {
    order: Order;
    product: Product;
    destination: Destination;
    dateOfStay: DateOfStay;
    quantity: number;
  }) {
    const {
      order,
      product,
      destination,
      dateOfStay,
      quantity,
    } = payload;

    const destinationSlug = destination.slug || destination.id;
    const productSlug = product.slug || product.id;
    
    if (!product.rental) {
      let url = `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }/conditions`;

      if (dateOfStay.isStartAndEndSet()) {
        url += `?start=${
          dateOfStay.start!.toISOString() }&end=${
          dateOfStay.end!.toISOString() }`;
      } else {
        url += `?start=${
          new Date().toISOString() }&end=${
          new Date().toISOString() }`;
      }

      url += `&quantity=${quantity}`;

      this.router.navigateByUrl(url);

      return;

    }

    const url = `widget/products/${
        productSlug}/destination/${
        destinationSlug }/order/${
        order.token }/conditions?start=${
        dateOfStay.start!.toISOString() }&end=${
        dateOfStay.end!.toISOString() }&quantity=${
        quantity}`;

    this.router.navigateByUrl(url);
  }

  navigateToCart(payload: {
    order: Order;
    destination: Destination;
    dateOfStay: DateOfStay | null;
  }) {
    const {
      order,
      destination,
      dateOfStay,
    } = payload;
    const destinationSlug = destination.slug || destination.id;
    const orderToken = order.token;

    const url = !isNil(dateOfStay) && dateOfStay.isStartAndEndSet() ?
      `widget/cart/destination/${
        destinationSlug}/order/${
        orderToken}?start=${
        dateOfStay.start!.toISOString()}&end=${
        dateOfStay.end!.toISOString()}` :
      `widget/cart/destination/${
        destinationSlug}/order/${
        orderToken}`;

    this.router.navigateByUrl(url);
  }

  navigateToGetStarted() {
    this.router.navigate(['/widget']);
  }

  navigateTo404(payload: {
    error: Error;
    url?: string;
  }) {
    const { error, url } = payload;

    this.router.navigate(['widget/404'], {
      skipLocationChange: true,
      queryParams: {
        url,
        errorMessage: error.message,
      },
    });
  }
}
